import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";

import { fetchWrapper } from "../../../helpers/fetchwrapper";
import ProductDetails from "../../ProductDetails/ProductDetails";

import {
  baseUrl,
  DayPickerStringsDE,
  DayPickerStringsEN,
} from "../../../helpers/constants";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import QrCodePage from "./QrCodePage";
import { IProduktDto } from "../../../Models/IProdukt";
import Header from "../../Layout/Header";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import {
  BoxRegular,
  DismissRegular,
  FilterRegular,
  IosArrowRtl24Regular,
  QrCodeRegular,
  SearchRegular,
} from "@fluentui/react-icons";
import {
  Button,
  Checkbox,
  Input,
  Field,
  Dropdown,
  Option,
  ProgressBar,
} from "@fluentui/react-components";
import useWindowSize from "../../../helpers/useWindowSize";
import { ILuebOption } from "../../../Models/ILuebOption";
import LuebPagedDataGrid from "../../../Controls/LuebPagedDataGrid";
import { ProductColumns } from "./ProductColumns";
import HeaderTextAnimation from "../../Layout/HeaderTextAnimation";

interface IProductsProps {}

function Products(props: IProductsProps) {
  const windowSize = useWindowSize();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [qrCodePageOpen, setQrCodePageOpen] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState<IProduktDto>();
  const [products, setProducts] = useState<{
    items: IProduktDto[] | undefined;
    itemCount: number;
    startItem: number;
  }>({
    items: [],
    itemCount: 0,
    startItem: 0,
  });
  const [queryValues, setQueryValues] = useState({
    systemId: "",
    auftragId: "",
    firma: "",
    datumVon: new Date(1999, 1, 1),
    datumBis: new Date(),
    systemsOnly: true,
    orderby: "",
    vsst: "",
    registeredSystemsOnly: false,
    startItem: 0,
  });
  const handleQueryValuesChange = (name: string, val: any) => {
    setQueryValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));
  };

  const [vsstOptions, setVsstOptions] = useState<ILuebOption[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageCount = Math.ceil(products.itemCount / 10);

  useEffect(() => {
    handleQueryValuesChange("startItem", currentPage * 10);
  }, [currentPage]);

  useEffect(() => {
    getProducts();
  }, [queryValues.startItem]);

  useEffect(() => {
    setCurrentPage(0);
  }, [products.itemCount]);

  useEffect(() => {
    setCurrentPage(0);
    if (currentPage == 0) {
      getProducts();
    }
  }, [queryValues.registeredSystemsOnly, queryValues.systemsOnly]);

  useEffect(() => {
    getVssts();
  }, []);

  useEffect(() => {
    if (products) {
      if (window.location) {
        if (window.location.search) {
          var search = window.location.search.split("=");
          var produktId = search[1];
          if (produktId) {
            setSelectedProduct(
              products.items?.find((x) => x.produktId == produktId)
                ? products.items.find((x) => x.produktId == produktId)
                : undefined
            );
          }
        } else {
          setSelectedProduct(undefined);
        }
      }
    }
  }, [products, window.location.search]);

  const columns = ProductColumns();

  const getVssts = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/product/GetVssts`)
      .then((data: string[]) => {
        let options: ILuebOption[] = [];
        data.forEach((vsst: string) => {
          options.push({ key: vsst, text: vsst });
        });
        setVsstOptions(options);
      })
      .catch((error) => {});
  };

  const getProducts = () => {
    setLoading(true);
    fetchWrapper
      .postWithAuth(`${baseUrl}/api/product/GetByQuery`, queryValues)
      .then((data) => {
        setProducts(data);
        setLoading(false);
      })
      .catch((error) => {
        setProducts({ items: [], itemCount: 0, startItem: 0 });
        setLoading(false);
      });
  };

  return (
    <div>
      <Header
        HTMLElement={
          <StackShim horizontal verticalAlign="center">
            <Button
              icon={<BoxRegular fontSize={24} color="#ff6600" />}
              size="large"
              appearance="subtle"
              onClick={() => {
                navigate("/sales/products");
              }}
            >
              {i18n.t<string>("products").toLocaleUpperCase()}
            </Button>
            {selectedProduct != undefined && (
              <>
                <IosArrowRtl24Regular color="#ff6600" />
                <Button appearance="transparent">
                  <HeaderTextAnimation
                    text={`${selectedProduct.produktId} - ${
                      i18n.language.includes("de")
                        ? selectedProduct.artikel?.benennungDE
                        : selectedProduct.artikel?.benennungEN
                    }`}
                  />
                </Button>
              </>
            )}
          </StackShim>
        }
      />
      <QrCodePage
        isOpen={qrCodePageOpen}
        items={products && products.items ? products?.items : []}
        onDismiss={() => setQrCodePageOpen(false)}
      />

      <div>
        {selectedProduct != undefined && (
          <ProductDetails
            salesView={true}
            product={selectedProduct}
            isPanelOpen={selectedProduct != undefined}
            onDismiss={() => navigate("/sales/proucts")}
            onRefresh={() => {}}
          />
        )}
        {!selectedProduct && (
          <>
            <div className="lueb-InnerContentBlockPadding2px">
              <>
                <Button
                  appearance="transparent"
                  icon={<SearchRegular />}
                  disabled={loading}
                  onClick={() => {
                    getProducts();
                  }}
                >
                  {i18n.t<string>("suchen")}
                </Button>
                <Button
                  appearance="transparent"
                  icon={<DismissRegular />}
                  disabled={loading}
                  onClick={() => {
                    setQueryValues({
                      systemId: "",
                      auftragId: "",
                      firma: "",
                      datumVon: new Date(1999, 1, 1),
                      datumBis: new Date(),
                      systemsOnly: true,
                      orderby: "",
                      vsst: "",
                      registeredSystemsOnly: false,
                      startItem: 0,
                    });
                  }}
                >
                  {i18n.t<string>("clearSearchParameter")}
                </Button>
                <Button
                  appearance="transparent"
                  icon={<QrCodeRegular />}
                  disabled={loading}
                  onClick={() => {
                    setQrCodePageOpen(true);
                  }}
                >
                  {i18n.t<string>("qrCodesForAllItems")}
                </Button>
              </>
            </div>
            <div className="lueb-ContentBlock">
              <h5>{i18n.t<string>("searchParams")}</h5>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                }}
              >
                <Field style={{ minWidth: "250px" }} label={"System-ID"}>
                  <Input
                    contentAfter={
                      queryValues.systemId == "" ? (
                        <FilterRegular />
                      ) : (
                        <DismissRegular
                          onClick={() => {
                            handleQueryValuesChange("systemId", "");
                          }}
                        />
                      )
                    }
                    disabled={loading}
                    value={queryValues.systemId}
                    name="systemId"
                    onChange={(ev, val) =>
                      handleQueryValuesChange("systemId", val.value)
                    }
                  />
                </Field>
                <Field style={{ minWidth: "250px" }} label={"Auftrag"}>
                  <Input
                    contentAfter={
                      queryValues.auftragId == "" ? (
                        <FilterRegular />
                      ) : (
                        <DismissRegular
                          onClick={() => {
                            handleQueryValuesChange("auftragId", "");
                          }}
                        />
                      )
                    }
                    disabled={loading}
                    value={queryValues.auftragId}
                    name="auftragId"
                    onChange={(ev, val) =>
                      handleQueryValuesChange("auftragId", val.value)
                    }
                  />
                </Field>

                <Field style={{ minWidth: "250px" }} label={"Firma"}>
                  <Input
                    contentAfter={
                      queryValues.firma == "" ? (
                        <FilterRegular />
                      ) : (
                        <DismissRegular
                          onClick={() => {
                            handleQueryValuesChange("firma", "");
                          }}
                        />
                      )
                    }
                    disabled={loading}
                    value={queryValues.firma}
                    name="firma"
                    onChange={(ev, val) =>
                      handleQueryValuesChange("firma", val.value)
                    }
                  />
                </Field>

                <Field style={{ minWidth: "250px" }} label={"Lieferdatum von"}>
                  <DatePicker
                    disabled={loading}
                    firstDayOfWeek={1}
                    value={queryValues.datumVon}
                    formatDate={(date?) => {
                      return date ? date.toLocaleDateString() : "";
                    }}
                    onSelectDate={(date) => {
                      date?.setHours(12);
                      handleQueryValuesChange("datumVon", date);
                    }}
                    placeholder="Select a date..."
                    strings={
                      i18n.language.includes("de")
                        ? DayPickerStringsDE
                        : DayPickerStringsEN
                    }
                  />
                </Field>

                <Field style={{ minWidth: "250px" }} label={"Lieferdatum bis"}>
                  <DatePicker
                    disabled={loading}
                    firstDayOfWeek={1}
                    value={queryValues.datumBis}
                    placeholder="Select a date..."
                    formatDate={(date?) => {
                      return date ? date.toLocaleDateString() : "";
                    }}
                    onSelectDate={(date) => {
                      date?.setHours(12);
                      handleQueryValuesChange("datumBis", date);
                    }}
                    strings={
                      i18n.language.includes("de")
                        ? DayPickerStringsDE
                        : DayPickerStringsEN
                    }
                  />
                </Field>

                <Field style={{ minWidth: "250px" }} label={"Vsst"}>
                  <Dropdown
                    disabled={loading}
                    onOptionSelect={(ev: any, val) => {
                      if (queryValues.vsst != val.optionValue) {
                        handleQueryValuesChange("vsst", val.optionValue);
                      } else {
                        handleQueryValuesChange("vsst", "");
                      }
                    }}
                    selectedOptions={[queryValues.vsst]}
                    value={
                      vsstOptions.find((x) => x.key == queryValues.vsst)
                        ?.text ?? ""
                    }
                  >
                    {vsstOptions.map((option) => {
                      return (
                        <Option value={option.key as string}>
                          {option.text}
                        </Option>
                      );
                    })}
                  </Dropdown>
                </Field>
                {windowSize.width <= 1400 && (
                  <>
                    <Field style={{ minWidth: "250px" }}>
                      <div></div>
                    </Field>
                    <Field style={{ minWidth: "250px" }}>
                      <div></div>
                    </Field>
                  </>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                <Field>
                  <Checkbox
                    disabled={loading}
                    checked={queryValues.systemsOnly}
                    name="systemsOnly"
                    label="Nur Systeme anzeigen"
                    onChange={(ev, val) => {
                      handleQueryValuesChange("systemsOnly", val.checked);
                    }}
                  />
                </Field>
                <Field>
                  <Checkbox
                    disabled={loading}
                    checked={queryValues.registeredSystemsOnly}
                    name="registeredSystemsOnly"
                    label="Nur registrierte Systeme anzeigen"
                    onChange={(ev, val) => {
                      handleQueryValuesChange(
                        "registeredSystemsOnly",
                        val.checked
                      );
                    }}
                  />
                </Field>
              </div>
            </div>
            {!loading && (
              <div className="lueb-ContentBlock">
                <LuebPagedDataGrid
                  sortable
                  resizableColumns={true}
                  columns={columns}
                  items={
                    products && products.items && !loading
                      ? products?.items
                      : []
                  }
                  columnSizingOptions={{
                    artikelId: { defaultWidth: 240, minWidth: 190 },
                    qrCodeId: { defaultWidth: 70, minWidth: 70 },
                    auftragId: { defaultWidth: 100, minWidth: 50 },
                    auftragpos: { defaultWidth: 80, minWidth: 80 },
                    subArtikelId: { defaultWidth: 115, minWidth: 115 },
                    "auftrag?.firma": { defaultWidth: 80, minWidth: 80 },
                    changeDate: { defaultWidth: 80, minWidth: 80 },
                    ausgangsdatum: { defaultWidth: 80, minWidth: 80 },
                    "toolboxReg?.firma": { defaultWidth: 180, minWidth: 180 },
                    "partnerReg?.firma": { defaultWidth: 180, minWidth: 180 },
                  }}
                  customTreffer={products.itemCount}
                  customPageCount={pageCount}
                  customCurrentPage={currentPage}
                  onPageChanged={(item) => {
                    setCurrentPage(item);
                  }}
                />
              </div>
            )}
            {loading && <ProgressBar thickness="large" />}
          </>
        )}
      </div>
    </div>
  );
}

export default Products;
